import { inject } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivateFn,
  Router,
  RouterStateSnapshot,
  UrlTree,
} from '@angular/router';
import { SessionStorageService } from '@core/session-storage.service';
import { map, Observable } from 'rxjs';
import { AccessTokenCodeRequest } from 'src/app/web-api-client';
import { CurrentUserService } from '.';

const POST_LOGIN_REDIRECT_URL_KEY = 'postLoginRedirectUrl';

export const authGuard: CanActivateFn = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot
) => {
  const session: SessionStorageService = inject(SessionStorageService);
  const router: Router = inject(Router);
  const currentUser: CurrentUserService = inject(CurrentUserService);

  // Handle Entra ID form_post return: exchange one-time sessionId for tokens
  const loginSessionId = route.queryParamMap.get('loginSessionId');
  if (loginSessionId && !session.has('session')) {
    return completeOAuthLoginSession(loginSessionId).pipe(
      map((authenticated: boolean) =>
        authenticated
          ? consumePostLoginRedirect(session, router, currentUser)
          : false
      )
    );
  }

  // Handle login error returned by the API after Entra ID callback
  const loginError = route.queryParamMap.get('loginError');
  if (loginError && !session.has('session')) {
    const message = decodeURIComponent(loginError.replace(/\+/g, ' '));
    currentUser.handleInvalidAccessToken(message);
    return false;
  }

  const req = new AccessTokenCodeRequest({
    code: route.queryParamMap.get('code') || '',
    state: route.queryParamMap.get('state') || '',
  });

  if (req.code && req.state && !session.has('session')) {
    return getAccessToken(req).pipe(
      map((authenticated: boolean) =>
        authenticated
          ? consumePostLoginRedirect(session, router, currentUser)
          : false
      )
    );
  }

  if (!session.has('session')) {
    session.set(POST_LOGIN_REDIRECT_URL_KEY, state.url);
    return router.parseUrl('/login');
  } else if (session.has('session')) {
    currentUser.checkCurrentUser();
    if (state.url === '/') {
      router.navigate([currentUser.defaultAuthenticatedRoute()]);
    }
  }

  return true;
};

function getAccessToken(req: AccessTokenCodeRequest): Observable<boolean> {
  const currentUser: CurrentUserService = inject(CurrentUserService);
  return currentUser.getAccessToken(req);
}

function completeOAuthLoginSession(
  loginSessionId: string
): Observable<boolean> {
  const currentUser: CurrentUserService = inject(CurrentUserService);
  return currentUser.completeOAuthLoginSession(loginSessionId);
}

function consumePostLoginRedirect(
  session: SessionStorageService,
  router: Router,
  currentUser: CurrentUserService
): UrlTree {
  const redirectUrl = session.get(POST_LOGIN_REDIRECT_URL_KEY) ?? '';
  session.remove(POST_LOGIN_REDIRECT_URL_KEY);

  // Returning one final tree avoids racing the original OAuth callback with
  // a second imperative navigation.
  const safeRedirectUrl =
    redirectUrl.startsWith('/') && !redirectUrl.startsWith('//')
      ? redirectUrl
      : currentUser.defaultAuthenticatedRoute();

  // Dashboard is the normal homepage, but a Country Admin is not allowed there.
  // Sending them there first raises "Access denied" and then bounces to /country.
  if (
    currentUser.isRestrictedCountryAdmin() &&
    isOutsideCountryAdminHome(safeRedirectUrl)
  ) {
    return router.parseUrl(currentUser.defaultAuthenticatedRoute());
  }

  return router.parseUrl(safeRedirectUrl);
}

function isOutsideCountryAdminHome(url: string): boolean {
  const path = url.split('?')[0];
  return (
    path === '/' ||
    path === '/dashboard' ||
    path.startsWith('/dashboard/') ||
    path === '/portfolio' ||
    path.startsWith('/portfolio/') ||
    path === '/entities' ||
    path.startsWith('/entities/') ||
    path === '/audit-history' ||
    path.startsWith('/audit-history/') ||
    path === '/tax-results' ||
    path.startsWith('/tax-results/') ||
    path === '/admin' ||
    path.startsWith('/admin/')
  );
}
